// Query keys are declared here, not written inline at the call site: a cache
// entry's identity retyped by hand is an invalidation that matches nothing.
export const ATTESTED_DOCUMENT = "ATTESTED_DOCUMENT";

export const attestedDocumentKey = (reference: string) =>
  [ATTESTED_DOCUMENT, reference] as const;

export const THEME = "THEME";
export const GROUP_THEME = "GROUP_THEME";

// The corporate goes in the key. Cached under a bare "theme", a payer who
// visits two schools in one session pays the second school on a page carrying
// the first school's name, colours and logo.
export const themeKey = (corporateName: string) =>
  [THEME, corporateName] as const;

// The group type alone is a two-value key space shared by every tenant of that
// type, so the entity id has to be in it or one subsidiary's branding is
// served to all of them.
export const groupThemeKey = (groupType: string, id: string | number) =>
  [GROUP_THEME, groupType, String(id)] as const;

export const PORTAL_CONTEXT = "PORTAL_CONTEXT";

// Both identifiers are in the key, and which one was supplied is part of it.
// A context cached under a bare "portal context" serves the first school's
// scope, theme and logo to the second school visited in the same session --
// the defect class the theme keys above already close, one layer up, where it
// decides who is being paid rather than only how the page looks.
export const portalContextKey = (entry: {
  slug?: string;
  paylink?: string;
}) =>
  [PORTAL_CONTEXT, entry.slug ?? "", entry.paylink ?? ""] as const;

export const SERVICE_CATALOGUE = "SERVICE_CATALOGUE";

// Every parameter that changes the response is in the key. `search` and `page`
// especially: cached under a key missing either, the catalogue serves the
// unfiltered first page as the answer to a search, which looks like a working
// search returning the wrong rows rather than like a cache fault.
export const serviceCatalogueKey = (criteria: {
  scopeType: string;
  scopeId?: number | null;
  search?: string;
  page?: number;
  perPage?: number;
}) =>
  [
    SERVICE_CATALOGUE,
    criteria.scopeType,
    criteria.scopeId ?? "",
    criteria.search ?? "",
    criteria.page ?? 1,
    criteria.perPage ?? 0,
  ] as const;

export const SCHOOL_FEE_STUDENT_SEARCH = "SCHOOL_FEE_STUDENT_SEARCH";
export const SCHOOL_FEE_CATALOGUE = "SCHOOL_FEE_CATALOGUE";
export const SCHOOL_FEE_FEE_ASSIGNMENT = "SCHOOL_FEE_FEE_ASSIGNMENT";

// Every parameter that changes the response is in the key. A search cached
// under a key missing `page` serves page 1's students for page 2.
export const schoolFeeStudentSearchKey = (
  coreServiceId: string,
  criteria: { matricule?: string; name?: string; page?: number; size?: number }
) =>
  [
    SCHOOL_FEE_STUDENT_SEARCH,
    coreServiceId,
    criteria.matricule ?? "",
    criteria.name ?? "",
    criteria.page ?? 1,
    criteria.size ?? 0,
  ] as const;

export const schoolFeeCatalogueKey = (
  coreServiceId: string,
  criteria: { page?: number; size?: number }
) =>
  [
    SCHOOL_FEE_CATALOGUE,
    coreServiceId,
    criteria.page ?? 1,
    criteria.size ?? 0,
  ] as const;

export const schoolFeeFeeAssignmentKey = (
  coreServiceId: string,
  selectionId: string
) => [SCHOOL_FEE_FEE_ASSIGNMENT, coreServiceId, selectionId] as const;

// The school-fee code list moved to constants/errorDecisions.ts. This module
// owns cache keys; that list is now one column of the decision table R-4
// requires, and splitting it across two files would have left the gate
// reading its subject from somewhere its own rules do not live.
